import React, { useState, useEffect } from 'react';
import { prewarmCosmicTextureCache } from '../canvas/planets/SolarTextures';

export default function CosmicLoader({ onLoaded }) {
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);

  useEffect(() => {
    // Pre-warm and cache all planetary canvas textures
    prewarmCosmicTextureCache((pct) => {
      setProgress(pct);
    });

    // Simulate smooth progress ramp-up to 100%
    const timer = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        setIsDone(true);
        if (onLoaded) onLoaded();
        setTimeout(() => setShouldRender(false), 600);
      }, 350);
    }, 400);

    return () => clearTimeout(timer);
  }, [onLoaded]);

  if (!shouldRender) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        background: '#090c10',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        opacity: isDone ? 0 : 1,
        pointerEvents: isDone ? 'none' : 'auto',
        transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Glowing Orbital Loader Core */}
      <div
        style={{
          position: 'relative',
          width: '90px',
          height: '90px',
          marginBottom: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer Pulsing Ring */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid rgba(67, 221, 208, 0.25)',
            borderTopColor: '#43DDD0',
            animation: 'loaderSpin 1.4s linear infinite',
            boxShadow: '0 0 25px rgba(67, 221, 208, 0.3)',
          }}
        />

        {/* Inner Counter-Rotating Ring */}
        <div
          style={{
            position: 'absolute',
            inset: '12px',
            borderRadius: '50%',
            border: '2px solid rgba(56, 189, 248, 0.2)',
            borderBottomColor: '#38bdf8',
            animation: 'loaderSpinReverse 1.8s linear infinite',
          }}
        />

        {/* Central Glowing Stellar Nucleus */}
        <div
          style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: '#ffffff',
            boxShadow: '0 0 16px #43DDD0, 0 0 32px #43DDD0',
            animation: 'stellarPulse 2s ease-in-out infinite',
          }}
        />
      </div>

      {/* Brand Title */}
      <div
        style={{
          fontSize: '0.82rem',
          fontWeight: 700,
          letterSpacing: '0.3em',
          color: '#F1F5F5',
          marginBottom: '8px',
        }}
      >
        LAZPLAY
      </div>

      {/* Subtitle Status */}
      <div
        style={{
          fontSize: '0.68rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#43DDD0',
          marginBottom: '16px',
        }}
      >
        INITIALIZING SOLAR UNIVERSE {progress}%
      </div>

      {/* Slim Progress Bar */}
      <div
        style={{
          width: '180px',
          height: '2px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '1px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(to right, #38bdf8, #43DDD0)',
            transition: 'width 0.2s ease',
            boxShadow: '0 0 8px #43DDD0',
          }}
        />
      </div>

      <style>
        {`
          @keyframes loaderSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes loaderSpinReverse {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }
          @keyframes stellarPulse {
            0%, 100% { transform: scale(1); opacity: 0.85; }
            50% { transform: scale(1.2); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}
